import React, { useEffect, useState, useRef, useCallback } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { AgentService } from "../../services/AgentService";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Button as ShadcnButton } from "../ui/button";
import { Tooltip, message } from "antd";
import AgentSessionOverview from "./AgentSessionOverview";
import { 
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import moment from "moment";
import { HistoryIcon } from "lucide-react";
import { AgentInternalRequestType } from "../../types/AgentInternalRequestType";

interface AgentSession {
  session_id: string;
  request_count: number;
  first_created: number;
  last_created: number;
  total_duration_ms: number;
  status: string;
  execution_mode: string;
  application_id: number | null;
  error_message: string | null;
}

interface PaginationInfo {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}



interface AgentSessionsTableProps {
  agentId: number;
  applicationId?: number;
}

const AgentSessionsTable: React.FC<AgentSessionsTableProps> = ({ agentId, applicationId }) => {
  const { apiKey } = usePermissions();
  const [searchParams] = useSearchParams();
  const version = searchParams.get("version");
  const [sessions, setSessions] = useState<AgentSession[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  
  // Use ref to track current pagination for interval
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;
  
  // Overview modal state
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [overviewSession, setOverviewSession] = useState<AgentSession | null>(null);

  const fetchSessions = useCallback((page: number = 1, pageSize: number = 10, isManualFetch: boolean = true) => {
    // Show loading state only for manual fetches to avoid UI flicker during auto-refresh
    if (isManualFetch) {
      setIsLoading(true);
    }
    
    const subscription = AgentService.getAgentSessions(
      agentId,
      page,
      pageSize,
      () => {},
      apiKey,
      version || undefined
    ).subscribe(
      (response: { sessions: AgentSession[]; pagination: PaginationInfo }) => {
        setSessions(response.sessions);
        setPagination(response.pagination);
        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching agent sessions:", error);
        // Only show error message for manual fetches, not automatic refetches
        if (isManualFetch) {
          message.error("Failed to load agent sessions");
        }
        setIsLoading(false);
      }
    );
    
    return subscription;
  }, [agentId, apiKey, version]);


  useEffect(() => {
    // Initial fetch (manual = true by default)
    const subscription = fetchSessions();
    
    // Set up interval for refetching every 10 seconds
    const intervalId = setInterval(() => {
      // Use ref to get current pagination values, mark as automatic fetch
      fetchSessions(paginationRef.current.page, paginationRef.current.page_size, false);
    }, 10000);
    
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
      clearInterval(intervalId);
    };
  }, [fetchSessions]);

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
      case "SUCCESS":
        return <CheckCircleOutlined className="text-green-500" />;
      case "FAILED":
      case "ERROR":
        return <CloseCircleOutlined className="text-red-500" />;
      case "PARTIAL_FAILURE":
        return <InfoCircleOutlined className="text-orange-500" />;
      case "RUNNING":
        return <ClockCircleOutlined className="text-blue-500" />;
      default:
        return <InfoCircleOutlined className="text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
      case "SUCCESS":
        return "green";
      case "FAILED":
      case "ERROR":
        return "red";
      case "PARTIAL_FAILURE":
        return "orange";
      case "RUNNING":
        return "blue";
      default:
        return "default";
    }
  };

  const formatDuration = (ms: number | null) => {
    if (ms == null) return "-";
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  const getOperationTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case AgentInternalRequestType.RESPOND.toLowerCase():
        return "green";
      case AgentInternalRequestType.ROUTE.toLowerCase():
        return "blue";
      case AgentInternalRequestType.ORCHESTRATE.toLowerCase():
        return "purple";
      default:
        return "default";
    }
  };

  const getStatusClasses = (status: string) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border";
    
    switch (status?.toLowerCase()) {
      case "active":
        return `${baseClasses} border-blue-500 text-blue-700 dark:text-white bg-card [background-image:linear-gradient(rgba(59,130,246,0.1),rgba(59,130,246,0.1))] dark:[background-image:linear-gradient(rgba(59,130,246,0.2),rgba(59,130,246,0.2))]`;
      case "complete":
      case "completed":
      case "succeeded":
      case "success":
        return `${baseClasses} border-green-500 text-green-700 dark:text-white bg-card [background-image:linear-gradient(rgba(34,197,94,0.1),rgba(34,197,94,0.1))] dark:[background-image:linear-gradient(rgba(34,197,94,0.2),rgba(34,197,94,0.2))]`;
      case "failed":
      case "error":
        return `${baseClasses} border-red-500 text-red-700 dark:text-white bg-card [background-image:linear-gradient(rgba(239,68,68,0.1),rgba(239,68,68,0.1))] dark:[background-image:linear-gradient(rgba(239,68,68,0.2),rgba(239,68,68,0.2))]`;
      case "running":
      case "in_progress":
        return `${baseClasses} border-blue-500 text-blue-700 dark:text-white bg-card [background-image:linear-gradient(rgba(59,130,246,0.1),rgba(59,130,246,0.1))] dark:[background-image:linear-gradient(rgba(59,130,246,0.2),rgba(59,130,246,0.2))]`;
      case "cancelled":
      case "canceled":
        return `${baseClasses} border-orange-500 text-orange-700 dark:text-white bg-card [background-image:linear-gradient(rgba(251,146,60,0.1),rgba(251,146,60,0.1))] dark:[background-image:linear-gradient(rgba(251,146,60,0.2),rgba(251,146,60,0.2))]`;
      default:
        return `${baseClasses} border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-card`;
    }
  };

  const getModeClasses = (mode: string) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border";
    return mode === "direct" 
      ? `${baseClasses} border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-card`
      : `${baseClasses} border-purple-500 text-purple-700 dark:text-white bg-card [background-image:linear-gradient(rgba(168,85,247,0.1),rgba(168,85,247,0.1))] dark:[background-image:linear-gradient(rgba(168,85,247,0.2),rgba(168,85,247,0.2))]`;
  };



  const handlePageChange = (newPage: number) => {
    fetchSessions(newPage, pagination.page_size);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    fetchSessions(1, newPageSize);
  };

  
  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-gc-med text-foreground">Agent Sessions</h3>
        <p className="text-sm text-muted-foreground">
          View all request sessions for this agent
        </p>
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
      
          {isLoading ? (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border hover:bg-transparent">
                  <TableHead className="w-[140px]">Session ID</TableHead>
                  <TableHead className="w-[180px]">Created</TableHead>
                  <TableHead className="w-[120px]">Status</TableHead>
                  <TableHead className="w-[100px]">Duration</TableHead>
                  <TableHead className="w-[100px]">Requests</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(5)].map((_, index) => (
                  <TableRow key={index} className="border-b border-border/50">
                    <TableCell className="py-4">
                      <div className="h-4 w-20 bg-muted animate-pulse rounded"></div>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="h-4 w-32 bg-muted animate-pulse rounded"></div>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="h-6 w-24 bg-muted animate-pulse rounded-full"></div>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="h-4 w-12 bg-muted animate-pulse rounded"></div>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="h-4 w-8 bg-muted animate-pulse rounded"></div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : sessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="space-y-3">
                <HistoryIcon className="w-16 h-16 text-muted-foreground mx-auto" />
                <h3 className="text-lg font-gc-med text-foreground">
                  No sessions found
                </h3>
                <p className="text-muted-foreground max-w-md">
                  Agent sessions will appear here when requests are processed through this agent.
                </p>
              </div>
            </div>
          ) : (
            <>
              <Table>
              <TableHeader>
                <TableRow className="border-b border-border hover:bg-transparent">
                  <TableHead className="w-[140px]">Session ID</TableHead>
                  <TableHead className="w-[180px]">Created</TableHead>
                  <TableHead className="w-[120px]">Status</TableHead>
                  <TableHead className="w-[100px]">Duration</TableHead>
                  <TableHead className="w-[100px]">Requests</TableHead>
                </TableRow>
              </TableHeader>
                <TableBody>
                  {sessions.map((session) => {
                    // Compute status once for this row
                    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
                    const isActive = session.last_created && session.last_created > fiveMinutesAgo;
                    const displayStatus = isActive ? "ACTIVE" : "COMPLETE";
                    
                    return (
                      <TableRow 
                        key={session.session_id} 
                        className="border-b border-border/50 cursor-pointer hover:bg-accent/50"
                        onClick={() => {
                          setOverviewSession(session);
                          setOverviewOpen(true);
                        }}
                      >
                        <TableCell className="py-4">
                          <Tooltip title={session.session_id}>
                            <span className="font-mono text-xs">{session.session_id.substring(0, 8)}...</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="py-4">
                          {session.first_created ? moment(session.first_created).format("MMM D, h:mm:ss A") : "-"}
                        </TableCell>
                        <TableCell className="py-4">
                          <span className={getStatusClasses(displayStatus)}>
                            {displayStatus}
                          </span>
                        </TableCell>
                        <TableCell className="py-4">
                          {formatDuration(session.total_duration_ms)}
                        </TableCell>
                        <TableCell className="py-4">
                          <span className="text-sm text-muted-foreground">{session.request_count}</span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              <div className="flex items-center justify-between px-2 py-4">
                <div className="text-sm text-muted-foreground">
                  Showing {(pagination.page - 1) * pagination.page_size + 1} to {Math.min(pagination.page * pagination.page_size, pagination.total)} of {pagination.total} sessions
                </div>
                <div className="flex items-center gap-2">
                  <ShadcnButton
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </ShadcnButton>
                  <span className="text-sm">
                    Page {pagination.page} of {Math.ceil(pagination.total / pagination.page_size)}
                  </span>
                  <ShadcnButton
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= Math.ceil(pagination.total / pagination.page_size)}
                  >
                    Next
                  </ShadcnButton>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      
      {overviewOpen && overviewSession && overviewSession.session_id && (
        <AgentSessionOverview
          open={overviewOpen}
          onClose={() => {
            setOverviewOpen(false);
            setOverviewSession(null);
          }}
          agentId={agentId}
          sessionId={overviewSession.session_id}
          applicationId={overviewSession.application_id || applicationId}
          executionMode={overviewSession.execution_mode}
        />
      )}
    </div>
  );
};

export default AgentSessionsTable; 